<template>
    <div
        class="overflow-hidden"
        v-bind:class="{'bg-white': styles.layout_style !== 'none', 'border border-gray-200 border-opacity-75': styles.layout_style === 'border', [styles.layout_style]: styles.layout_style !== 'border', [styles.corners]: true}"
    >
        <div v-if="hasData" class="py-6 border-b border-gray-200 border-opacity-75 px-6 sm:px-8">
            <div class="grid gap-4"
                :class="hasCustomLinks ? 'lg:grid-cols-3' : 'lg:grid-cols-2'"
            >
                <div class="relative">
                    <date-range-picker
                        class="flex-1"
                        :date_to="filter.date_range.to"
                        :date_from="filter.date_range.from"
                        @update="dateRangeUpdate"
                        :ranges="datepickerRanges"
                    ></date-range-picker>
                    <div class="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none text-opacity-50">
                        <svg v-if="styles.icons === 'bold'" class="w-4 h-4" fill="currentColor"
                             xmlns="http://www.w3.org/2000/svg"
                             viewBox="0 0 24 24" width="24" height="24">
                            <path fill="none" d="M0 0h24v24H0z"/>
                            <path
                                d="M2 11h20v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-9zm15-8h4a1 1 0 0 1 1 1v5H2V4a1 1 0 0 1 1-1h4V1h2v2h6V1h2v2z"/>
                        </svg>
                        <svg v-if="styles.icons !== 'bold'" class="w-4 h-4" fill="currentColor"
                             xmlns="http://www.w3.org/2000/svg"
                             viewBox="0 0 24 24" width="24" height="24">
                            <path fill="none" d="M0 0h24v24H0z"/>
                            <path
                                d="M17 3h4a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4V1h2v2h6V1h2v2zm-2 2H9v2H7V5H4v4h16V5h-3v2h-2V5zm5 6H4v8h16v-8z"/>
                        </svg>
                    </div>
                </div>
                <div :hidden="!hasCustomLinks">
                    <select
                        v-if="hasCustomLinks"
                        v-model="filter.report_custom_links"
                        name="select"
                        @change="filterChanged"
                        multiple
                        multiselect-max-items="2"
                        class="block w-full rounded sm:text-sm focus:ring-0 focus:border-gray-300"
                    >
                        <option value="all">All links</option>
                        <option value="default">Default link</option>

                        <option v-for="customLinkKey in custom_referral_links" :value="customLinkKey">
                            Link: {{ customLinkKey }}
                        </option>
                    </select>
                    <select
                        v-else
                        v-model="filter.report_custom_links"
                    >
                        <option value="all">All links</option>
                    </select>
                </div>
                <div>
                    <div class="flex space-x-px">
                        <btn class="flex-grow rounded-r-none" :type="btnTypes.yearly"
                             @click="setGroupType('yearly')"
                        >
                            {{ translations.yearly ?? 'Yearly' }}
                        </btn>
                        <btn class="flex-grow rounded-r-none rounded-l-none" :type="btnTypes.monthly"
                             @click="setGroupType('monthly')"
                        >
                            {{ translations.monthly ?? 'Monthly' }}
                        </btn>
                        <btn class="flex-grow rounded-l-none" :type="btnTypes.daily"
                             @click="setGroupType('daily')"
                        >
                            {{ translations.daily ?? 'Daily' }}
                        </btn>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex flex-col relative" v-if="!hasData">
            <div class="flex absolute inset-0 justify-center items-center font-medium bg-white bg-opacity-50">
                <div class="flex flex-col justify-center items-center text-center py-20">
                    <div class="p-4 bg-white rounded opacity-75">
                        <div class="flex justify-center items-center pb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                <path fill="none" d="M0 0H24V24H0z"/>
                                <path
                                    d="M5 3v16h16v2H3V3h2zm15.293 3.293l1.414 1.414L16 13.414l-3-2.999-4.293 4.292-1.414-1.414L13 7.586l3 2.999 4.293-4.292z"/>
                            </svg>
                        </div>
                        <h2 class="text-lg font-semibold mb-2">
                            {{ translations.visualReports ?? 'Visual reports live here' }}
                        </h2>
                        <p class="text-sm tracking-wide opacity-60">
                            {{ translations.visualReportsNotice ?? 'When referral data comes in, you’ll see reporting here.' }}
                        </p>
                    </div>
                </div>
            </div>
            <table class="min-w-full divide-y divide-gray-200 divide-opacity-75">
                <thead>
                <tr>
                    <th scope="col" class="py-8 pr-4 pl-8 opacity-60">
                        <div class="w-full h-3 bg-gray-200 rounded-lg mb-2"></div>
                        <div class="w-full h-3 bg-gray-200 rounded-lg"></div>
                    </th>
                    <th scope="col" class="px-8 py-4 opacity-60">
                        <div class="w-full h-3 bg-gray-200 rounded-lg mb-2"></div>
                        <div class="w-full h-3 bg-gray-200 rounded-lg"></div>
                    </th>
                    <th scope="col" class="px-8 py-4 opacity-60">
                        <div class="w-full h-3 bg-gray-200 rounded-lg mb-2"></div>
                        <div class="w-full h-3 bg-gray-200 rounded-lg"></div>
                    </th>
                    <th scope="col" class="py-8 pr-8 pl-4 opacity-60">
                        <div class="w-full h-3 bg-gray-200 rounded-lg mb-2"></div>
                        <div class="w-full h-3 bg-gray-200 rounded-lg"></div>
                    </th>
                </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200 divide-opacity-75">
                <tr class="hover:bg-gray-50">
                    <td class="py-4 pr-4 pl-8">
                        <div class="h-3 bg-gray-200 w-6/12 rounded-lg mb-1.5"></div>
                        <div class="w-10/12 h-3 bg-gray-200 bg-opacity-70 rounded-lg"></div>
                    </td>
                    <td class="px-4 py-4">
                        <div class="w-6/12 h-4 bg-indigo-100 rounded-lg"></div>
                    </td>
                    <td class="px-4 py-4">
                        <div class="w-6/12 h-3 bg-gray-200 rounded-lg"></div>
                    </td>
                    <td class="py-4 pr-8 pl-4">
                        <div class="w-6/12 h-3 bg-gray-200 rounded-lg"></div>
                    </td>
                </tr>
                <tr class="hover:bg-gray-50">
                    <td class="py-4 pr-4 pl-8">
                        <div class="h-3 bg-gray-200 w-6/12 rounded-lg mb-1.5"></div>
                        <div class="w-10/12 h-3 bg-gray-200 bg-opacity-70 rounded-lg"></div>
                    </td>
                    <td class="px-4 py-4">
                        <div class="w-6/12 h-4 bg-indigo-100 rounded-lg"></div>
                    </td>
                    <td class="px-4 py-4">
                        <div class="w-6/12 h-3 bg-gray-200 rounded-lg"></div>
                    </td>
                    <td class="py-4 pr-8 pl-4">
                        <div class="w-6/12 h-3 bg-gray-200 rounded-lg"></div>
                    </td>
                </tr>
                <tr class="hover:bg-gray-50">
                    <td class="py-4 pr-4 pl-8">
                        <div class="h-3 bg-gray-200 w-6/12 rounded-lg mb-1.5"></div>
                        <div class="w-10/12 h-3 bg-gray-200 bg-opacity-70 rounded-lg"></div>
                    </td>
                    <td class="px-4 py-4">
                        <div class="w-6/12 h-4 bg-indigo-100 rounded-lg"></div>
                    </td>
                    <td class="px-4 py-4">
                        <div class="w-6/12 h-3 bg-gray-200 rounded-lg"></div>
                    </td>
                    <td class="py-4 pr-8 pl-4">
                        <div class="w-6/12 h-3 bg-gray-200 rounded-lg"></div>
                    </td>
                </tr>
                <tr class="hover:bg-gray-50">
                    <td class="py-4 pr-4 pl-8">
                        <div class="h-3 bg-gray-200 w-6/12 rounded-lg mb-1.5"></div>
                        <div class="w-10/12 h-3 bg-gray-200 bg-opacity-70 rounded-lg"></div>
                    </td>
                    <td class="px-4 py-4">
                        <div class="w-6/12 h-4 bg-indigo-100 rounded-lg"></div>
                    </td>
                    <td class="px-4 py-4">
                        <div class="w-6/12 h-3 bg-gray-200 rounded-lg"></div>
                    </td>
                    <td class="py-4 pr-8 pl-4">
                        <div class="w-6/12 h-3 bg-gray-200 rounded-lg"></div>
                    </td>
                </tr>
                <tr>
                    <td colspan="4"
                        class="p-4 text-sm font-medium text-center text-opacity-100 hover:bg-gray-50 hover:underline text-{{ is_custom_color($link_color) ? 'custom-link' : $link_color }}">
                        <div class="mx-auto w-4/12 h-3 bg-gray-200 rounded-lg"></div>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <div class="flex flex-col px-6 py-2" v-if="hasData">
            <apexchart
                v-if="report"
                :width="report.width"
                :height="report.height"
                :type="report.type"
                :options="report.options"
                :series="report.series"
            ></apexchart>
        </div>
    </div>
</template>
<script>
import moment from 'moment-timezone';
import { formatNumber } from '../../../helpers/common.js';

export default {
    name: "ReferralAnalytics",
    props: ['data_url', 'default_styles', 'has_data', 'translations', 'custom_referral_links', 'program_name'],
    data() {
        return {
            errors: [],
            loading: false,
            hasData: this.has_data ?? true,
            styles: {
                layout: this.default_styles.layout ?? 'one',
                icons: this.default_styles.icons ?? 'outline',
                corners: this.default_styles.corners ?? 'rounded',
                text_color: this.default_styles.text_color ?? 'gray-900',
                layout_style: this.default_styles.layout_style ?? 'shadow',
                navigation_color: this.default_styles.navigation_color ?? 'brand',
                portal_layout_button_color: this.default_styles.portal_layout_button_color ?? 'black'
            },
            filter: {
                group_type: 'monthly',
                report_custom_links: ['all'],
                date_range: {
                    from: moment.utc().subtract(1, 'month'),
                    to: moment()
                }
            },
            btnTypes: {
                yearly: 'light',
                monthly: this.default_styles.portal_layout_button_color,
                daily: 'light'
            },
            report: false,
            datepickerRanges: {
                'Today': [moment(), moment()],
                'Yesterday': [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
                'Last 7 Days': [moment().subtract(6, 'days'), moment()],
                'Last 30 Days': [moment().subtract(29, 'days'), moment()],
                'This Month': [moment().startOf('month'), moment().endOf('month')],
                'Last Month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
                'Current Year': [moment.utc().startOf('year'), moment()]
            }
        }
    },
    computed: {
        hasCustomLinks() {
            return this.custom_referral_links.length > 0
        }
    },
    beforeCreate() {
        window.MultiselectDropdownOptions = {
            style:{
                width: '100%',
                height: '2.32rem',
                padding: '5px 8px',
            },
            checkboxColor: this.default_styles.portal_layout_button_color
        }
    },
    created() {
        if (this.hasData) {
            this.refreshData();
        }
    },
    methods: {
        formatNumber,
        refreshData() {

            this.loading = true;

            axios
                .post(this.data_url, {filter: this.filter})
                .then(response => {
                    const status = parseInt(response.data.status || -1);
                    if (status === 1) {
                        this.report = response.data.data;
                        this.setChartOptions();
                    }
                })
                .catch(error => {
                    this.errors = error.response?.data?.errors || [];
                })
                .finally(() => this.loading = false);
        },
        setChartOptions() {
            this.report.options.xaxis = {
                ...this.report.options.xaxis.categories,
                labels: {
                    formatter: (value) => this.hasCustomLinks ? this.formatNameBasedOnValue(value) : ''
                }
            };
            this.report.options.yaxis = {
                labels: {
                    formatter: (value) => formatNumber(value)
                }
            };
            this.report.options.chart.toolbar = {
                show: true,
                offsetX: 0,
                offsetY: 0,
                tools: {
                    download: true,
                },
                export: {
                    csv: {
                        filename: this.getFileName(),
                        columnDelimiter: ',',
                        headerCategory: 'category',
                        headerValue: 'value',
                        dateFormatter(timestamp) {
                            return new Date(timestamp).toDateString()
                        }
                    },
                    svg: {
                        filename: this.getFileName(),
                    },
                    png: {
                        filename: this.getFileName(),
                    }
                }
            }
        },
        getFileName() {
           return this.program_name
               + '_' +
               (this.filter.date_range.from instanceof moment
                   ? this.filter.date_range.from.format('YYYY-MM-DD')
                   : this.filter.date_range.from)
               + '_' +
               (this.filter.date_range.to instanceof moment
                   ? this.filter.date_range.to.format('YYYY-MM-DD')
                   : this.filter.date_range.to)
        },
        formatNameBasedOnValue(value) {
            switch (value) {
                case 'all': return 'All'
                case 'default': return 'Default'
                default: return value
            }
        },
        filterChanged() {
            this.refreshData();
        },
        setGroupType(groupType) {
            this.filter.group_type = groupType;

            this.btnTypes = {
                yearly: 'light',
                monthly: 'light',
                daily: 'light'
            }
            this.btnTypes[groupType] = this.styles.portal_layout_button_color;

            this.filterChanged();
        },
        dateRangeUpdate(ev, picker) {
            this.filter.date_range.from = picker.startDate.format('YYYY-MM-DD');
            this.filter.date_range.to = picker.endDate.format('YYYY-MM-DD');
            this.filterChanged();
        }
    }
}
</script>

